/**
 * Session detail body (tutoring).
 */
import * as React from 'react';
import {
  Calendar,
  Clock,
  Building2,
  BookOpen,
  Users,
  Tag,
  MapPin,
  Video,
  GraduationCap,
  User,
  UserCircle,
  Minus,
  HeartHandshake,
  Repeat2,
  ArrowDown,
} from 'lucide-react';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailsCard } from '@/components/common/DetailsCard';
import { ParticipantChip } from '@/components/common/ParticipantChip';
import {
  ATTENDANCE_STATUS_CONFIG,
  PAIR_TYPE_CONFIG,
  SESSION_RECURRENCE_CONFIG,
  SESSION_TYPE_CONFIG,
  SESSION_WEEKDAY_LABELS,
} from '@/components/sessions/session-configs';
import type { SessionWithParticipants, SessionParticipant } from '@/types/inperson/session.types';
import type { AssignmentSource } from '@/types/inperson/pair.types';
import type { AttendanceStatus } from '@/types/attendance.types';
import {
  calcDurationMinutes,
  createStudentsAttendanceMap,
  deriveSessionParticipants,
  formatSessionTime,
  getInPersonStatusConfig,
  getSessionFormatLabel,
  getStatusColor,
  groupStudentsBySource,
  parseSessionGroupDate,
  type AttendanceCount,
  type StudentWithSource,
  type UserForDisplay,
} from '@/components/sessions/inperson/session-table-utils';

/** Badge for how a pair was assigned (manual, auto, etc.). */
const SourceBadge: React.FC<{ source: AssignmentSource }> = ({ source }) => {
  const cfg = PAIR_TYPE_CONFIG[source] || PAIR_TYPE_CONFIG.manual;
  const Icon = cfg.icon;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border"
      style={{
        backgroundColor: `color-mix(in srgb, ${cfg.badgeColor} 15%, transparent)`,
        color: cfg.badgeColor,
        borderColor: cfg.badgeColor,
      }}
      title={cfg.label}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
      <span>{cfg.label}</span>
    </span>
  );
};

/** Tutor-to-students pair card with attendance chips. */
const PairDisplay: React.FC<{
  tutorUser: UserForDisplay;
  studentUsers: UserForDisplay[];
  assignmentSource: AssignmentSource;
  tutorParticipantRole?: 'tutor' | 'volunteer';
  tutorAttendanceStatus?: AttendanceStatus;
  tutorAbsenceJustification?: string | null;
  studentsAttendanceData?: Map<string, { status: AttendanceStatus; justification?: string | null }>;
}> = ({
  tutorUser,
  studentUsers,
  assignmentSource,
  tutorParticipantRole = 'tutor',
  tutorAttendanceStatus,
  tutorAbsenceJustification,
  studentsAttendanceData,
}) => (
  <div
    className="rounded-lg border-2 p-3 relative"
    style={{
      background: 'color-mix(in srgb, var(--color-2-nuance-1) 3%, transparent)',
      borderColor: 'color-mix(in srgb, var(--color-2-nuance-1) 20%, transparent)',
    }}
  >
    <div className="absolute top-2 right-2">
      <SourceBadge source={assignmentSource} />
    </div>
    <div className="mb-3">
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center border"
          style={{
            background: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
            borderColor: 'var(--color-2-nuance-1)',
          }}
        >
          <GraduationCap className="w-4 h-4" style={{ color: 'var(--color-2-nuance-1)' }} />
        </div>
        <div className="flex-1 pr-20">
          <ParticipantChip
            user={{
              firstName: tutorUser.firstName,
              lastName: tutorUser.lastName,
              photoUrl: tutorUser.photoUrl,
            }}
            role={tutorParticipantRole}
            size="md"
            showTooltip
            attendanceStatus={tutorAttendanceStatus}
            absenceJustification={tutorAbsenceJustification}
          />
        </div>
      </div>
    </div>
    <div className="flex items-center justify-center my-2">
      <div
        className="h-px flex-1"
        style={{
          background: 'linear-gradient(to right, var(--color-2-nuance-1), var(--color-3-nuance-1))',
          opacity: 0.3,
        }}
      />
      <ArrowDown
        className="mx-2 w-4 h-4"
        style={{ color: 'var(--color-1-nuance-1)', opacity: 0.5 }}
      />
      <div
        className="h-px flex-1"
        style={{
          background: 'linear-gradient(to right, var(--color-2-nuance-1), var(--color-3-nuance-1))',
          opacity: 0.3,
        }}
      />
    </div>
    <div>
      <div className="flex items-start gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0"
          style={{
            background: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
            borderColor: 'var(--color-3-nuance-1)',
          }}
        >
          <User className="w-4 h-4" style={{ color: 'var(--color-3-nuance-1)' }} />
        </div>
        <div className="flex-1 flex flex-wrap gap-2">
          {studentUsers.map((student) => {
            const studentAttendance = studentsAttendanceData?.get(student.id);
            return (
              <ParticipantChip
                key={student.id}
                user={{
                  firstName: student.firstName,
                  lastName: student.lastName,
                  displayName: student.displayName,
                  photoUrl: student.photoUrl,
                }}
                role="student"
                size="md"
                showTooltip
                attendanceStatus={studentAttendance?.status}
                absenceJustification={studentAttendance?.justification}
              />
            );
          })}
        </div>
      </div>
    </div>
  </div>
);

/** Present / absent / excused / pending counts for the session. */
const AttendanceStatusBadges: React.FC<{ countByStatus: AttendanceCount }> = ({
  countByStatus,
}) => {
  const hasAnyStatus =
    countByStatus.present > 0 || countByStatus.absent > 0 || countByStatus.excused > 0;
  if (!hasAnyStatus) return null;

  return (
    <div className="flex flex-wrap gap-2 pb-4 border-b border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]">
      {(['present', 'absent', 'excused', 'pending'] as const).map((key) => {
        const count = countByStatus[key];
        const config = ATTENDANCE_STATUS_CONFIG[key];
        if (count === 0 || !config) return null;
        return (
          <span
            key={key}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border-2"
            style={{
              background: `color-mix(in srgb, ${config.badgeColor} 10%, transparent)`,
              color: config.badgeColor,
              borderColor: config.badgeColor,
            }}
          >
            <span className="font-bold">{count}</span> {config.label}
          </span>
        );
      })}
    </div>
  );
};

/** Grid of student-tutor pairs on the tutoring session details. */
const PairSection: React.FC<{
  pairsByTutor: Map<string, StudentWithSource[]>;
  session: SessionWithParticipants;
  gridClassName?: string;
}> = ({ pairsByTutor, session, gridClassName }) => {
  if (pairsByTutor.size === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <HeartHandshake className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
        <h4 className="text-sm font-semibold text-[var(--color-1-nuance-1)]">
          Binômes Élève-Tuteur
        </h4>
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full border"
          style={{
            background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
            color: 'var(--color-2-nuance-1)',
            borderColor: 'var(--color-2-nuance-1)',
          }}
        >
          {pairsByTutor.size}
        </span>
      </div>
      <div className={gridClassName ?? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'}>
        {Array.from(pairsByTutor.entries()).map(([tutorId, studentsData]) => {
          const tutorParticipant = session.participants?.find(
            (p) => p.userId === tutorId && (p.role === 'tutor' || p.role === 'volunteer'),
          );
          const pairWithTutor = session.studentTutorPairs?.find((p) => p.tutorUserId === tutorId);
          const tutorUserForPair = tutorParticipant?.user ?? pairWithTutor?.tutorUser;

          if (!tutorUserForPair) return null;

          const tutorChipRole = tutorParticipant?.role === 'volunteer' ? 'volunteer' : 'tutor';
          const studentsBySource = groupStudentsBySource(studentsData);
          const studentsAttendanceMap = createStudentsAttendanceMap(
            studentsData,
            session.participants,
          );

          return Array.from(studentsBySource.entries()).map(([source, studentsForSource], idx) => (
            <PairDisplay
              key={`${tutorId}-${source}-${idx}`}
              tutorUser={tutorUserForPair}
              studentUsers={studentsForSource}
              assignmentSource={source as AssignmentSource}
              tutorParticipantRole={tutorChipRole}
              tutorAttendanceStatus={
                (tutorParticipant?.attendanceStatus as AttendanceStatus) || 'pending'
              }
              tutorAbsenceJustification={tutorParticipant?.absenceJustification}
              studentsAttendanceData={studentsAttendanceMap}
            />
          ));
        })}
      </div>
    </div>
  );
};

/** Participants who are not in a student-tutor pair. */
const UnmatchedParticipantsSection: React.FC<{
  unmatchedTutors: SessionParticipant[];
  unmatchedVolunteers: SessionParticipant[];
  unmatchedStudents: SessionParticipant[];
  hasPairs: boolean;
}> = ({ unmatchedTutors, unmatchedVolunteers, unmatchedStudents, hasPairs }) => {
  const hasUnmatched =
    unmatchedTutors.length > 0 || unmatchedStudents.length > 0 || unmatchedVolunteers.length > 0;

  if (!hasUnmatched) return null;

  const hasUnmatchedTutors = unmatchedTutors.length > 0;
  const hasUnmatchedVsc = unmatchedVolunteers.length > 0;

  return (
    <div>
      {hasPairs && (
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-[var(--color-1-nuance-1)]">
            Autres Participants
          </h4>
        </div>
      )}
      <div className="participants-layout">
        {hasUnmatchedTutors && (
          <div className="participants-group">
            <div className="participants-group-header">
              <GraduationCap className="w-3.5 h-3.5" />
              <span>
                {unmatchedTutors.length === 1
                  ? getRoleLabel('tutor')
                  : getRoleLabelPlural('tutor').toLowerCase()}
              </span>
              <span className="participants-group-count">{unmatchedTutors.length}</span>
            </div>
            <div className="participant-chip-list">
              {unmatchedTutors.map((tutor) => (
                <ParticipantChip
                  key={tutor.id}
                  user={tutor.user}
                  role="tutor"
                  size="md"
                  showTooltip
                  attendanceStatus={(tutor.attendanceStatus as AttendanceStatus) || 'pending'}
                  absenceJustification={tutor.absenceJustification}
                />
              ))}
            </div>
          </div>
        )}

        {hasUnmatchedVsc && (
          <div className="participants-group">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-3.5 h-3.5 text-[var(--color-3-nuance-1)]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-3-nuance-1)]">
                VSC
              </span>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full border"
                style={{
                  background: 'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
                  color: 'var(--color-3-nuance-1)',
                  borderColor: 'var(--color-3-nuance-1)',
                }}
              >
                {unmatchedVolunteers.length}
              </span>
            </div>
            <div className="participant-chip-list">
              {unmatchedVolunteers.map((volunteer) => (
                <ParticipantChip
                  key={volunteer.id}
                  user={volunteer.user}
                  role="volunteer"
                  volunteerRoleLabel={getRoleLabel('volunteer')}
                  size="md"
                  showTooltip
                />
              ))}
            </div>
          </div>
        )}

        {(hasUnmatchedTutors || hasUnmatchedVsc) && unmatchedStudents.length > 0 && (
          <div className="participants-divider" aria-hidden="true" />
        )}

        {unmatchedStudents.length > 0 && (
          <div className="participants-group">
            <div className="participants-group-header participants-group-header--student">
              <UserCircle className="w-3.5 h-3.5" />
              <span>
                {unmatchedStudents.length === 1
                  ? getRoleLabel('student')
                  : getRoleLabelPlural('student')}
              </span>
              <span className="participants-group-count participants-group-count--student">
                {unmatchedStudents.length}
              </span>
            </div>
            <div className="participant-chip-list">
              {unmatchedStudents.map((student) => (
                <ParticipantChip
                  key={student.id}
                  user={student.user}
                  role="student"
                  levelId={student.user?.userProfiles?.[0]?.levelId}
                  size="md"
                  showTooltip
                  attendanceStatus={(student.attendanceStatus as AttendanceStatus) || 'pending'}
                  absenceJustification={student.absenceJustification}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Status + type header for a tutoring session detail. */
export const SessionDetailHeader: React.FC<{ session: SessionWithParticipants }> = ({
  session,
}) => {
  const statusConfig = getInPersonStatusConfig(session.status, session.type);
  const StatusIcon = statusConfig.icon;
  const typeConfig = SESSION_TYPE_CONFIG[session.type];
  const TypeIcon = typeConfig?.icon;
  const typeColorName = typeConfig?.colorName ?? 'color-2';
  const establishmentLabel = session.establishmentName ?? session.establishment?.name;
  const parsedDate = parseSessionGroupDate(String(session.sessionDate ?? ''));
  const dayName = format(parsedDate, 'EEEE', { locale: fr });
  const dateFormatted = format(parsedDate, 'd MMMM yyyy', { locale: fr });

  return (
    <div className="details-view-header">
      <div className="details-view-header-left">
        <div className="details-view-header-icon">
          <Calendar className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
        </div>
        <div className="details-view-header-content">
          <div className="details-view-header-with-badges">
            <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)] capitalize">
              {dayName} {dateFormatted}
            </h3>
            <DetailBadge
              label={typeConfig?.label ?? session.type}
              icon={TypeIcon}
              colorName={typeColorName}
              variant="filled"
            />
            <DetailBadge
              label={statusConfig.label}
              icon={StatusIcon}
              colorName={getStatusColor(session.status)}
              variant="outlined"
            />
            {establishmentLabel && (
              <DetailBadge
                label={establishmentLabel}
                icon={Building2}
                colorName="color-1"
                variant="outlined"
              />
            )}
          </div>
          {session.topic && (
            <p className="text-base text-[var(--color-1-nuance-2)]">{session.topic}</p>
          )}
        </div>
      </div>
    </div>
  );
};

/** Date, establishment, subjects, and format cards. */
export const SessionInfoCards: React.FC<{ session: SessionWithParticipants }> = ({ session }) => {
  const establishmentLabel = session.establishmentName ?? session.establishment?.name;
  const sessionDate = format(
    parseSessionGroupDate(String(session.sessionDate ?? '')),
    'EEEE d MMMM yyyy',
    { locale: fr },
  );
  const showRemoteLink =
    session.joinLink && (session.sessionFormat === 'remote' || session.sessionFormat === 'hybrid');
  const hasActivityOrSubjects =
    session.activityName ||
    session.activity ||
    (session.subjectNames && session.subjectNames.length > 0) ||
    (session.levelNames && session.levelNames.length > 0);

  return (
    <>
      <DetailsCard icon={Clock} title="Date et heure" colorName="color-2" colSpan={1}>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Date</p>
            <p className="text-base font-semibold text-foreground capitalize">{sessionDate}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Horaires</p>
            <div className="time-pill">
              <Clock className="w-4 h-4" />
              <span>
                {formatSessionTime(session.startTime)} - {formatSessionTime(session.endTime)}
              </span>
            </div>
          </div>

          {(session.durationMinutes ?? calcDurationMinutes(session.startTime, session.endTime)) !=
            null && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Durée</p>
              <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] text-[var(--color-1-nuance-1)] border border-[var(--color-1-nuance-1)]">
                {session.durationMinutes ?? calcDurationMinutes(session.startTime, session.endTime)}{' '}
                minutes
              </span>
            </div>
          )}
        </div>
      </DetailsCard>

      <DetailsCard icon={MapPin} title="Lieu et format" colorName="color-1" colSpan={1}>
        <div className="space-y-4">
          {establishmentLabel && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Établissement</p>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
                <p className="text-base font-semibold text-foreground">{establishmentLabel}</p>
              </div>
            </div>
          )}

          {session.sessionFormat && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Format</p>
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] text-[var(--color-1-nuance-1)] border-2 border-[var(--color-1-nuance-1)]">
                {getSessionFormatLabel(session.sessionFormat)}
              </span>
            </div>
          )}

          {showRemoteLink ? (
            <div>
              <a
                href={session.joinLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-2-nuance-1)] text-white hover:opacity-90 transition-opacity"
              >
                <Video className="w-4 h-4" />
                Rejoindre la session
              </a>
            </div>
          ) : (
            (session.sessionFormat === 'remote' || session.sessionFormat === 'hybrid') && (
              <EmptyPlaceholder message="Aucun lien de visioconférence configuré" />
            )
          )}
        </div>
      </DetailsCard>

      {session.mode === 'recurring' && (
        <DetailsCard
          icon={Repeat2}
          title="Planification récurrente"
          colorName="color-2"
          colSpan={1}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Mode</p>
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] text-[var(--color-2-nuance-1)] border-2 border-[var(--color-2-nuance-1)]">
                Récurrente
              </span>
            </div>

            {session.recurrenceType && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Type de récurrence</p>
                <p className="text-base font-semibold text-foreground">
                  {SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.label ||
                    session.recurrenceType}
                </p>
              </div>
            )}

            {session.recurrenceType === 'several_days_per_week' && session.recurrenceRule && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  Jours de récurrence
                </p>
                <div className="flex flex-wrap gap-2">
                  {(
                    ((session.recurrenceRule as { weekDays?: number[] })?.weekDays as
                      | number[]
                      | undefined) || []
                  ).map((day) => (
                    <span
                      key={day}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] text-[var(--color-1-nuance-1)] border border-[var(--color-1-nuance-1)]"
                    >
                      {SESSION_WEEKDAY_LABELS[day] || `Jour ${day}`}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {session.recurrenceGroupId && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Série</p>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] text-[var(--color-2-nuance-1)] border border-[var(--color-2-nuance-1)]">
                  <Repeat2 className="w-3.5 h-3.5" />
                  Série récurrente
                </span>
              </div>
            )}
          </div>
        </DetailsCard>
      )}

      <DetailsCard icon={Tag} title="Activité, niveaux et matières" colorName="color-2" colSpan={1}>
        {hasActivityOrSubjects ? (
          <div className="space-y-4">
            {Boolean(session.activityName || session.activity) && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Activité</p>
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] text-[var(--color-2-nuance-1)] border border-[var(--color-2-nuance-1)]">
                  {session.activityName || session.activity?.label}
                </span>
                {Boolean(session.activity?.maxStudents) && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Maximum: {session.activity.maxStudents} élèves
                  </p>
                )}
              </div>
            )}

            {session.levelNames && session.levelNames.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Niveaux</p>
                <div className="flex flex-wrap gap-2">
                  {session.levelNames.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] text-[var(--color-1-nuance-1)] border border-[var(--color-1-nuance-1)]"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {session.subjectNames && session.subjectNames.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Matières</p>
                <div className="flex flex-wrap gap-2">
                  {session.subjectNames.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-[color-mix(in_srgb,var(--color-3-nuance-1)_10%,transparent)] text-[var(--color-3-nuance-1)] border border-[var(--color-3-nuance-1)]"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyPlaceholder message="Aucune activité ou matière associée" />
        )}
      </DetailsCard>
    </>
  );
};

/** Internal notes card (hidden when empty). */
export const SessionNotesCard: React.FC<{ session: SessionWithParticipants }> = ({ session }) => (
  <DetailsCard icon={BookOpen} title="Notes internes" colorName="color-1" colSpan="full">
    {session.internalNotes ? (
      <div className="rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_5%,transparent)] p-4">
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
          {session.internalNotes}
        </p>
      </div>
    ) : (
      <EmptyPlaceholder message="Aucune note interne pour cette séance" />
    )}
  </DetailsCard>
);

/** Tutors / students / pairs for a tutoring session detail. */
export const SessionParticipantsContent: React.FC<{
  session: SessionWithParticipants;
  pairGridClassName?: string;
}> = ({ session, pairGridClassName }) => {
  const {
    pairsByTutor,
    unmatchedTutors,
    unmatchedVolunteers,
    unmatchedStudents,
    countByStatus,
    hasParticipants,
    hasPairs,
  } = deriveSessionParticipants(session);

  if (!hasParticipants) {
    return (
      <div className="empty-state">
        <Users className="w-10 h-10 text-muted-foreground opacity-40" />
        <p className="empty-state-text">Aucun participant pour le moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AttendanceStatusBadges countByStatus={countByStatus} />
      <PairSection
        pairsByTutor={pairsByTutor}
        session={session}
        gridClassName={pairGridClassName}
      />
      <UnmatchedParticipantsSection
        unmatchedTutors={unmatchedTutors}
        unmatchedVolunteers={unmatchedVolunteers}
        unmatchedStudents={unmatchedStudents}
        hasPairs={hasPairs}
      />
    </div>
  );
};

/** Created / updated timestamps. */
export const SessionDetailFooter: React.FC<{ session: SessionWithParticipants }> = ({ session }) =>
  session.createdAt ? (
    <div className="mt-8 pt-4 border-t border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]">
      <div className="flex items-center gap-6 text-xs text-muted-foreground">
        <span>Créé le {new Date(session.createdAt).toLocaleDateString('fr-FR')}</span>
        {session.updatedAt && (
          <>
            <Minus className="w-3 h-3" />
            <span>Modifié le {new Date(session.updatedAt).toLocaleDateString('fr-FR')}</span>
          </>
        )}
      </div>
    </div>
  ) : null;

/** Full tutoring-session detail body (header, cards, participants, notes). */
export const TutoringSessionContent: React.FC<{ session: SessionWithParticipants }> = ({
  session,
}) => (
  <>
    <SessionDetailHeader session={session} />
    <div className="details-view-grid">
      <SessionInfoCards session={session} />
      <DetailsCard icon={Users} title="Participants" colorName="color-3" colSpan="full">
        <SessionParticipantsContent session={session} />
      </DetailsCard>
      <SessionNotesCard session={session} />
    </div>
    <SessionDetailFooter session={session} />
  </>
);
